import {
  APP_INITIALIZER,
  EnvironmentProviders,
  InjectionToken,
  Provider,
} from '@angular/core';
import { USER_ACCOUNT_SETTINGS_FEATURE_KEY } from './data-access/state/user-account-settings.feature';
import { userAccountSettingsReducer } from './data-access/state/user-account-settings.reducer';
import { provideState } from '@ngrx/store';
import { HttpClient } from '@angular/common/http';
import { TranslateService, TranslationObject } from '@ngx-translate/core';
import { provideEffects } from '@ngrx/effects';
import { catchError, firstValueFrom, of } from 'rxjs';
import {
  changeEmailRequest$,
  changePhoneProcess$,
  confirmCode$,
  loadUserProfile$,
  loadUserSettings$,
  startUpdateUserSettingsProcess$,
  updateUserSettingsRequest$,
  startUpdateTheme$,
  updateThemeRequest$,
  startUpdateLanguage$,
  updateLanguageRequest$,
  startUpdateWeeklySubscription$,
  updateWeeklySubscriptionRequest$,
  startUpdateGlobalSearchSettings$,
  updateGlobalSearchSettingsRequest$,
  startChangeEmailProcess$,
  startChangePhoneProcess$,
  startConfirmCode$,
  updateDevicePageView$,
  startUpdateUserProfile$,
  updateUserProfile$,
  showMessage$,
} from './data-access/state/user-account-settings.effects';
import { UserSettingsModels } from '@share-models';

const shareUserAccountSettingsLangs = Object.values(
  UserSettingsModels.LANGUAGE,
) as UserSettingsModels.LANGUAGE[];

export function shareUserAccountSettingsTranslationsInitializer(
  translateService: TranslateService,
  http: HttpClient,
): () => Promise<void> {
  return () => {
    const requests = shareUserAccountSettingsLangs.map((lang) => {
      const url = `assets/share-user-account-settings/i18n/${lang}.json`;
      return firstValueFrom(
        http
          .get<TranslationObject>(url)
          .pipe(catchError(() => of({} as TranslationObject))),
      ).then((translations) => {
        translateService.setTranslation(lang, translations ?? {}, true);
      });
    });

    return Promise.all(requests).then(() => undefined);
  };
}

export interface ShareUserAccountSettingsConfig {
  apiUrl: string;
  userManagerUrl: string;
}

export const SHARE_USER_ACCOUNT_SETTINGS_CONFIG =
  new InjectionToken<ShareUserAccountSettingsConfig>(
    'SHARE_USER_ACCOUNT_SETTINGS_CONFIG',
  );

export function provideShareUserAccountSettings(
  config: ShareUserAccountSettingsConfig,
): (Provider | EnvironmentProviders)[] {
  return [
    {
      provide: SHARE_USER_ACCOUNT_SETTINGS_CONFIG,
      useValue: config,
    },
    provideEffects({
      startUpdateUserProfile$,
      updateUserProfile$,
      startChangeEmailProcess$,
      changeEmailRequest$,
      startChangePhoneProcess$,
      changePhoneProcess$,
      startConfirmCode$,
      confirmCode$,
      updateDevicePageView$,
      loadUserProfile$,
      loadUserSettings$,
      startUpdateUserSettingsProcess$,
      updateUserSettingsRequest$,
      startUpdateTheme$,
      updateThemeRequest$,
      startUpdateLanguage$,
      updateLanguageRequest$,
      startUpdateWeeklySubscription$,
      updateWeeklySubscriptionRequest$,
      startUpdateGlobalSearchSettings$,
      updateGlobalSearchSettingsRequest$,
      showMessage$,
    }),
    provideState(USER_ACCOUNT_SETTINGS_FEATURE_KEY, userAccountSettingsReducer),
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: shareUserAccountSettingsTranslationsInitializer,
      deps: [TranslateService, HttpClient],
    },
  ];
}
